<template>
    <div 
        class="workflow-form workflow-form-formulate"
        :class="[
            localDisabled ? 'workflow-form-disabled' : '',
        ]"
    >
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
        <FormulateForm
            ref="theFormulateForm"
            :name="formName"
            v-model="localValues"
            :schema="localSchema"
            @created="handleFormCreated"
            @input="handleFormInput"
            @submit="handleSubmit"
            @submit-raw="handleSubmitRaw"
            #default="{ isLoading }"
        >  <!-- @cancel="handleCollectDataCancel"  moved to button itself, but this works if adding button using form schema -->
            <!-- :keep-model-data="true" works fine -- but the data still gets sent!  don't want that --> 
            <div v-if="!localDisabled" class="mt-4 d-flex justify-content-between gap-2">
                <div>
                    <div v-if="disabled && !previewMode" class="font-weight-bold text-uppercase">Read Only</div>
                </div>
                <div>
                    <!-- <FormulateInput
                        type="button"
                        label="Cancel"
                        :outer-class="['minimal']"
                        @click="handleCollectDataCancel"
                        :disabled="isLoading"
                    /> -->
                    <!-- Show save as draft?  then use submit-raw event to save to different endopint or something -->
                    <!-- Modal header (and therefore close button) is hidden so we must always show this -->
                    <FormulateInput
                        v-if="!localDisabled"
                        type="submit"
                        name="submit"
                        :label="isLoading ? 'Loading...' : 'Save Progress'"
                        :disabled="isLoading || localDisabled"
                    />
                </div>
            </div>
        </FormulateForm>

    </div>
</template>

<script>
// THIS IS FOR A SINGLE FORM -- BUT CAN HAVE MULTIPLE PAGES
// BUT EVERYTHING HERE IS SUBMITTED TOGETHER

import { errorHandling } from '../../js/custom-vue-mixins';
import cloneDeep from 'lodash/cloneDeep';
import { suppliosApi } from '../../js/api-service';
import debounce from 'lodash/debounce';

export default {
    name: 'WorkflowFormFormulate',
    components: {},
    mixins: [
        errorHandling,
    ],
    props: {
        schema: {
            type: Array,
            required: true,
        },
        values: {  // existing values, if any
            type: Object,
            required: true,
        },
        taskId: {  // used in submit endpoint
            type: String,
            required: true,
        },
        name: {  // used as formName IF PROVIDED (optional) - otherwise we use `${this.taskId}form` as formName
            // USE THIS IF YOU HAVE MULTIPLE OF THESE COMPONENTS SHOWN AT ONCE (NOW LIKELY, SINCE YOU USE THIS TO RENDER EXTRA STUFF IN WORKFLOW TASKS)
            type: String,
            required: false,
        },
        fileCategory: {  // if saving files, need this to know S3File category.... or all same?
            type: String,
            required: false,
        },
        disabled: {  // disable the ENTIRE form - we have separate previewMode which hides submit button also
            type: Boolean,
            default: false,
        },
        previewMode: {  // will also set localDisabled=true
            type: Boolean,
            default: false,  
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            localSchema: null,  // modify schema (if needed) during create and set here
            localValues: null,  // the v-model with form
            localIsValid: null,  
        }
    },
    computed: {
        formName() {
            return this.name || `${this.taskId}form`;
        },
        localDisabled() {
            // if props disabled or previewMode are true
            return this.disabled || this.previewMode;
        },
    },
    created() {
        this.debouncedUpdateIsValid = debounce(this.updateIsValid, 250);
        this.localSchema = this.prepSchema(this.schema);
        this.localValues = this.prepValues(this.values);
    },
    mounted() {
        
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        prepSchema(incomingSchema) {
            // just a hook point for doing anything to schema during created()
            // don't set it here -- just return it so you can be specific about where it gets set
            // if disabled, we have to manually set on each one?
            const newSchema = cloneDeep(incomingSchema);
            const canBeDisabled = (obj) => {
                // can we set .disabled = true on this object? - should do this for any Formulate field, or other things that can be 'disabled' / have disabled prop
                console.log('checking canBeDisabled on', obj);
                if (typeof obj !== 'object' || Array.isArray(obj)) {
                    // not all things that have typeof obj == 'object' can be disabled, but certainly anything NOT typeof 'object' should not be disabled by us
                    console.log('canBeDisabled = false');
                    return false;  
                }
                if (obj.component) {
                    // if the object has a 'component' key, then its not a Formulate field
                    // NOTE - might be some other non-formulate object that we still want to disable... handle those later by putting more logic in here
                    // for now, if we have component key, we'll NOT set disabled
                    console.log('canBeDisabled = false');
                    return false;
                }
                // otherwise we'll assume its a Formulate field, and can set 'disabled = true'
                // should we look specifically for a 'name' field?  other characteristic things?
                console.log('canBeDisabled = TRUE');
                return true;  
            };
            const setDisabled = (componentObj) => {
                // recursively go through all components to set disabled=true
                // ONLY actually set disabled = true if you DO NOT have 'component' key in the object -- this way we only do it on formulate fields
                //if (!componentObj.component) {  // NEW July 14 2022 - added better logic around figuring out if something is a Formulate field or other component, or string, etc.
                if (canBeDisabled(componentObj)) {
                    console.log('canBeDisabled is true for', componentObj);
                    componentObj.disabled = true;
                }
                if (componentObj.children && Array.isArray(componentObj.children) && (componentObj.children.length > 0)) {
                    componentObj.children.forEach(child => setDisabled(child));
                }
            };
            if (this.localDisabled) {
                newSchema.forEach(comp => setDisabled(comp));
            }
            /// recursively make sure we have validation-name set - use label as default instead of name if not set
            // maybe you can make this more efficient later instead of going through recursively twice
            const isFormulateFieldObj = (obj) => {
                if (typeof obj !== 'object' || Array.isArray(obj) || obj.component) {
                    return false;
                } else {
                    return true;  // for now, this should tell the difference
                }
            };
            const ensureValidationName = (obj) => {
                if (isFormulateFieldObj(obj)) {
                    if (!obj.validationName && !obj['validation-name']) {
                        // if validationName is not already set, set it to obj.label (if we have it)
                        obj.validationName = obj.label || obj.name;  // by default, formulate takes name first, then label which is strange...
                    }
                }
                if (obj.children && Array.isArray(obj.children)) {
                    obj.children.forEach(child => ensureValidationName(child));
                }
            };
            newSchema.forEach(comp => ensureValidationName(comp));
            return newSchema;
        },
        prepValues(incomingValues) {
            // just a hook point for doing anything to values during created() 
            // don't set it here, just return it so you can be specific about where/when it gets set in local data
            return cloneDeep(incomingValues);
        },
        async submitFromParent() {
            // used named form function to submit from parent
            await this.$formulate.submit(this.formName);
            return true;
        },
        async handleSubmit(submitData) {
            // called from @submit event
            // TODO TODO TODO - enable submit_partial_form like you did on formio component
            // don't proceed if localDisabled is set (via previewMode or disabled props)
            if (this.localDisabled) {
                console.error(`Cannot submit WorkflowFormFormulate form name ${this.formName} because localDisabled is true`);
                return;
            }
            this.loading = true;
            try {
                const { data } = await suppliosApi.workflowTask.submit_complete_form(this.taskId, submitData);
                // data coming back should be the same as submitted, but maybe with things cleaned, calculated, etc.
                // so its wise to set that as localData here also - do it with v-model?  or named form .setValues(formName, values)?
                console.log(data);
                // this.localValues = Object.assign({}, data);  // cannot just set the response back on the localValues - must either run through prepValues again, or similar
                this.$emit('submitted', data);
            } catch (err) {
                this.apiErrorHandler(err);
            }
        },
        handleFormCreated() {
            this.debouncedUpdateIsValid();
        },
        handleFormInput() {
            // has values in payload - but we just want to check validation
            this.debouncedUpdateIsValid();
        },
        handleSubmitRaw(submitData) {
            // called from @submit-raw event on form
            console.log('from submit-raw', submitData);
        },
        updateIsValid() {
            // NOTE - this should be called in a debounced wrapper, set in created() - otherwise the form isn't done updating yet
            // also, make things work if ref isn't found on page.... since its debounced, it can get called when form is already gone
            // run through each form, check its validation, and update it in registry
            // we'll get stuff from each one's .formContext -- seems safest?  its part of the API
            const form = this.$refs['theFormulateForm'];
            const hasErrors = form?.hasErrors;
            this.localIsValid = !hasErrors;
            this.$emit('is-valid', this.localIsValid);
        },
    }
}
</script>

<style lang="scss">

.formulate-section {
    border: 1px solid var(--gray-200);
    border-radius: 0.5rem;
    padding: 1rem;
    .formulate-section-heading {
        color: var(--primary-darker);
        text-transform: uppercase;
        font-weight: 500;
    }
}

.workflow-form {

    ul.nav.nav-pills {
        font-weight: 600;
    }

    .tab-content {
        margin-top: 1.5rem;
    }

    &.workflow-form-disabled {

        input, select {
            color: #000;
            // border: none;  // looks weird
        }

        .formulate-input-group-add-more,
        a.formulate-input-group-repeatable-remove {
            display: none !important;  // hide the buttons when disabled
        }
    }
}
</style>